import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { EsignatureTokenStatus } from '~/redux/api/methods/tickets/esignature/getTokenStatus';
import * as selectors from '~/redux/selectors';
import { Nullable } from '~/types/Nullable';
import { testIsFileExtensionSupported } from '~/utils/esignature';
import {
  getTicketDocumentFileExtension,
  testHasInvalidSignersEsignRevamp,
} from '~/utils/ticket';

import {
  EsignatureRecipient,
  EsignatureTokenStatusValue,
} from '../types/esignature';

export const useSendToEsignatureTooltip = ({
  esignatureTokenStatus,
  isLoadingTokenStatus,
  signers,
}: {
  esignatureTokenStatus: Nullable<EsignatureTokenStatus> | undefined;
  isLoadingTokenStatus: boolean;
  signers: EsignatureRecipient[];
}) => {
  const intl = useIntl();
  const ticket = useSelector(selectors.selectTicketState);
  const fileExtension = getTicketDocumentFileExtension(ticket);
  const hasEsignatureIntegration = !!esignatureTokenStatus;
  const esignatureProvider = esignatureTokenStatus?.providerType;

  if (!hasEsignatureIntegration && !isLoadingTokenStatus) {
    return intl.formatMessage({
      id: 'SendToEsignature.noEsignatureIntegrationTooltip',
      defaultMessage:
        'This account has not set up an e-signature provider yet.',
      description:
        'Tooltip for the send to e-signature button when there is no e-signature integration setup.',
    });
  }

  if (testHasInvalidSignersEsignRevamp(signers)) {
    return intl.formatMessage({
      id: 'SendToEsignature.invalidSignersTooltip',
      defaultMessage:
        'Please fix the invalid signers before sending to e-signature.',
      description:
        'Tooltip for the send to e-signature button when there are invalid signers',
    });
  }

  if (
    esignatureProvider &&
    !testIsFileExtensionSupported(fileExtension, esignatureProvider)
  ) {
    return intl.formatMessage({
      id: 'SendToEsignature.fileTypeNotSupportedTooltip',
      defaultMessage:
        'The file type is not supported by your e-signature provider. Please upload your contract in a supported file type and try again.',
      description:
        'Tooltip for the send to e-signature button when the file type is not supported by the e-signature provider',
    });
  }

  if (
    esignatureTokenStatus?.accountToken === EsignatureTokenStatusValue.Dead ||
    esignatureTokenStatus?.envelopeToken === EsignatureTokenStatusValue.Dead
  ) {
    return intl.formatMessage({
      id: 'SendToEsignature.problemWithEsignatureTokenTooltip',
      defaultMessage: 'There is a problem with your e-signature connection.',
      description:
        'Tolltip for the send to e-signature button when there is a problem with the e-signature integration.',
    });
  }

  return undefined;
};
